"use client";

import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/use-fetch";
import { notifyAccountChanged } from "@/lib/account";
import { Badge, Button, Card, EmptyState, Input, Modal, PageHeader, Skeleton } from "@/components/ui";
import { useToast } from "@/components/toast";
import type { Account, CreditTransaction, Invoice, PackagePayment, Subscription, SubscriptionPackage } from "@/lib/types";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}
function money(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function payTone(s: string): "green" | "red" | "gray" {
  if (s === "APPROVED") return "green";
  if (s === "FAILED") return "red";
  return "gray";
}

// Package store: browse the catalogue and pay for a package with an EVC / mobile wallet (WaafiPay).
// On an approved payment the account is credited immediately.
function PackageStore({ defaultWallet }: { defaultWallet?: string | null }) {
  const toast = useToast();
  const { data: catalogData, loading: catalogLoading } = useFetch<{ packages: SubscriptionPackage[] }>("packages");
  const [rows, setRows] = useState<PackagePayment[] | null>(null);
  const [selected, setSelected] = useState<SubscriptionPackage | null>(null);
  const [wallet, setWallet] = useState("");
  const [idemKey, setIdemKey] = useState("");
  const [paying, setPaying] = useState(false);

  const packages = (catalogData?.packages ?? []).filter((p) => p.isActive !== false);

  const load = useCallback(() => {
    api<{ payments: PackagePayment[] }>("account/package-payments")
      .then((r) => setRows(r.payments || []))
      .catch(() => setRows([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  function openPay(p: SubscriptionPackage) {
    setSelected(p);
    setWallet((defaultWallet || "").replace(/\D/g, ""));
    // One key per purchase attempt. If the request is retried (double-click, flaky network) the
    // gateway sees the same key and replays the original outcome instead of charging again.
    setIdemKey(crypto.randomUUID());
  }

  async function pay() {
    if (!selected) return;
    const accountNo = wallet.replace(/\D/g, "");
    if (accountNo.length < 7) {
      toast.error("Enter a valid mobile wallet number.");
      return;
    }
    setPaying(true);
    try {
      const res = await api<{ creditsAdded: number | null }>("account/packages/purchase", {
        method: "POST",
        body: { packageCode: selected.packageCode, accountNo, idempotencyKey: idemKey }
      });
      toast.success(`Payment approved — ${(res.creditsAdded ?? 0).toLocaleString()} credits added.`);
      setSelected(null);
      setWallet("");
      load();
      notifyAccountChanged();
    } catch (err) {
      // Decline reason (e.g. insufficient balance) comes straight from the wallet.
      toast.error((err as Error).message);
      // A declined attempt is settled, so a genuine retry must use a fresh key.
      setIdemKey(crypto.randomUUID());
    } finally {
      setPaying(false);
    }
  }

  function priceOf(p: SubscriptionPackage): number {
    return p.finalPriceCents ?? p.priceCents ?? 0;
  }

  return (
    <Card title="Buy or renew a package" bodyClassName="p-5 space-y-5">
      {catalogLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : packages.length === 0 ? (
        <EmptyState title="No packages available" description="Contact your account manager for a package." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {packages.map((p) => {
            const hasDiscount = (p.discountPercent ?? 0) > 0;
            return (
              <div key={p.packageCode} className="flex flex-col rounded-xl border border-gray-200 p-4">
                <p className="font-semibold text-gray-900">{p.packageName || p.name || p.packageCode}</p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-brand-700 dark:text-brand-300">
                  {(p.creditUnits ?? 0).toLocaleString()}
                  <span className="ml-1 text-xs font-medium text-gray-400">credits</span>
                </p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-gray-900">{money(priceOf(p))}</span>
                  {hasDiscount && p.priceCents != null && p.priceCents !== priceOf(p) && (
                    <span className="text-xs text-gray-400 line-through">{money(p.priceCents)}</span>
                  )}
                  {hasDiscount && <Badge tone="green">-{p.discountPercent}%</Badge>}
                </div>
                <p className="mt-1 text-xs text-gray-400">Valid {p.validityDays ?? 0} days</p>
                {p.description && <p className="mt-1 text-xs text-gray-500">{p.description}</p>}
                <div className="mt-3">
                  <Button size="sm" className="w-full" onClick={() => openPay(p)}>Buy with EVC</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rows && rows.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Payment history</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4 font-medium">Package</th>
                  <th className="py-2 pr-4 font-medium text-right">Credits</th>
                  <th className="py-2 pr-4 font-medium text-right">Paid</th>
                  <th className="py-2 pr-4 font-medium">Wallet</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.packagePaymentId} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-medium text-gray-900">{r.packageName || r.packageCode}</td>
                    <td className="py-2 pr-4 text-right tabular-nums text-gray-600">{(r.creditUnits ?? 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right tabular-nums text-gray-600">{money(r.amountCents ?? 0)}</td>
                    <td className="py-2 pr-4 text-xs text-gray-500">{r.accountNo || "—"}</td>
                    <td className="py-2 pr-4">
                      <Badge tone={payTone(r.status)}>{r.status}</Badge>
                      {r.status === "FAILED" && r.responseMsg && <p className="mt-0.5 text-xs text-gray-400">{r.responseMsg}</p>}
                    </td>
                    <td className="py-2 pr-4 text-xs text-gray-500">{fmtDate(r.createdDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        open={Boolean(selected)}
        onClose={() => (paying ? null : setSelected(null))}
        title="Pay with EVC"
        footer={
          <>
            <Button variant="secondary" disabled={paying} onClick={() => setSelected(null)}>Cancel</Button>
            <Button disabled={paying} onClick={pay}>
              {paying ? "Processing…" : `Pay ${money(selected ? (selected.finalPriceCents ?? selected.priceCents ?? 0) : 0)}`}
            </Button>
          </>
        }
      >
        {selected && (
          <div className="space-y-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:bg-gray-500/10">
              <p className="font-semibold text-gray-900">{selected.packageName || selected.name || selected.packageCode}</p>
              <p className="mt-0.5 text-sm text-gray-600">
                {(selected.creditUnits ?? 0).toLocaleString()} credits · {money(selected.finalPriceCents ?? selected.priceCents ?? 0)} · valid {selected.validityDays ?? 0} days
              </p>
            </div>
            <Input
              label="Mobile wallet number"
              value={wallet}
              onChange={(e) => setWallet(e.target.value.replace(/\D/g, "").slice(0, 15))}
              inputMode="tel"
              placeholder="e.g. 615xxxxxx"
              autoFocus
            />
            <p className="text-xs text-gray-500">
              You&apos;ll get a prompt on this number to approve the payment. Your credits are added the moment it&apos;s approved.
            </p>
            {paying && <p className="text-xs text-brand-600 dark:text-brand-300">Waiting for you to approve the payment on your phone…</p>}
          </div>
        )}
      </Modal>
    </Card>
  );
}
function txTone(t: string): "green" | "red" | "gray" {
  if (["TOPUP", "GRANT", "REFUND"].includes(t)) return "green";
  if (t === "CHARGE") return "red";
  return "gray";
}
function invTone(s: string): "green" | "amber" | "gray" | "red" {
  if (s === "PAID") return "green";
  if (s === "ISSUED") return "amber";
  if (s === "VOID") return "red";
  return "gray";
}

export default function BillingPage() {
  const { data: account, loading } = useFetch<Account>("account");
  const { data: txData } = useFetch<{ transactions: CreditTransaction[] }>("credit/transactions?limit=25");
  const { data: invData } = useFetch<{ invoices: Invoice[] }>("invoices?limit=25");

  const prepaid = account?.accountType !== "POSTPAID";
  // CreditBalance mirrors the package balance; any excess is spendable wallet credit.
  const spendable = Math.max(account?.creditBalance ?? 0, account?.totalPackageBalance ?? 0);
  const subs = (account?.subscriptions ?? []).filter((s: Subscription) => s.status === "ACTIVE");
  const transactions = txData?.transactions ?? [];
  const invoices = invData?.invoices ?? [];

  return (
    <div className="space-y-6">
      <PageHeader title="Billing" subtitle="Your balance, package subscriptions, credit history and invoices." />

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{prepaid ? "SMS credit" : "Billing"}</p>
          {loading ? (
            <Skeleton className="mt-2 h-9 w-28" />
          ) : (
            <p className="mt-1 text-3xl font-bold tabular-nums text-gray-900">
              {prepaid ? spendable.toLocaleString() : "Postpaid"}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-400">
            {prepaid ? "Available to send" : "Billed per usage"}
          </p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Active packages</p>
          <p className="mt-1 text-3xl font-bold tabular-nums text-gray-900">{subs.length}</p>
          <p className="mt-1 text-xs text-gray-400">Current subscriptions</p>
        </Card>
      </div>

      {prepaid && !loading && spendable <= 0 && (
        <div className="rounded-xl border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800 dark:border-warning-500/30 dark:bg-warning-500/10 dark:text-warning-200">
          You have no credit to send with. Buy a package below to top up.
        </div>
      )}

      <PackageStore defaultWallet={account?.ownerMsisdn ?? null} />

      {subs.length > 0 && (
        <Card title="Active packages">
          <div className="grid gap-3 sm:grid-cols-2">
            {subs.map((s) => {
              const total = s.creditUnitsTotal ?? 0;
              const remaining = s.creditUnitsRemaining ?? 0;
              const pct = total > 0 ? Math.round((remaining / total) * 100) : 0;
              return (
                <div key={s.userPackageSubscriptionId} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">{s.packageName || s.packageCode || "Package"}</p>
                    <Badge tone="blue">{remaining.toLocaleString()} left</Badge>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">
                    {remaining.toLocaleString()} / {total.toLocaleString()} credits · expires {fmtDate(s.expiresAt)}
                  </p>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Credit history">
          {transactions.length === 0 ? (
            <EmptyState title="No transactions" description="Charges, top-ups and adjustments appear here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-4 font-medium">Type</th>
                    <th className="py-2 pr-4 font-medium text-right">Amount</th>
                    <th className="py-2 pr-4 font-medium text-right">Balance</th>
                    <th className="py-2 pr-4 font-medium">When</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <tr key={t.creditTransactionId} className="border-b border-gray-100">
                      <td className="py-2 pr-4"><Badge tone={txTone(t.type)}>{t.type}</Badge></td>
                      <td className={`py-2 pr-4 text-right tabular-nums ${t.amount < 0 ? "text-danger-600" : "text-success-600 dark:text-success-400"}`}>
                        {t.amount > 0 ? "+" : ""}{t.amount.toLocaleString()}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums text-gray-600">{t.balanceAfter?.toLocaleString() ?? "—"}</td>
                      <td className="py-2 pr-4 text-xs text-gray-500">{fmtDate(t.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Invoices">
          {invoices.length === 0 ? (
            <EmptyState title="No invoices" description="Issued invoices will appear here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-4 font-medium">Period</th>
                    <th className="py-2 pr-4 font-medium text-right">Amount</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.invoiceId} className="border-b border-gray-100">
                      <td className="py-2 pr-4 text-xs text-gray-600">{fmtDate(inv.periodStart)} – {fmtDate(inv.periodEnd)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-gray-800">{money(inv.amountCents)}</td>
                      <td className="py-2 pr-4"><Badge tone={invTone(inv.status)}>{inv.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
